import { BankAccountStatus, useWealthOverview } from '@egw/api'
import { GenericError, PageHeader } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES, type FeatureFlags } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { useCallback, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { type Config } from '../../config/config'

export const SettingsPage: FC = () => {
  const { t } = useTranslation('features-settings')
  const config = configClient.getConfig() as Config
  const { flags } = useFlags<FeatureFlags>()

  const navigate = useNavigate()

  const onClickHandler = useCallback(
    (route: string) => {
      void navigate(route)
    },
    [navigate],
  )

  const { data: wealthOverviewData, isLoading, isError } = useWealthOverview()

  const checkingAccount = wealthOverviewData?.highYieldChecking?.accounts[0]

  const checkingAccountClosed = checkingAccount?.status === BankAccountStatus.CLOSED

  const optionClassName =
    'w-full flex py-xs px-2xs border-list flex-row justify-between items-center typo-primary-lg-regular text-text-body'

  if (isLoading) {
    return (
      <div className="egw-container">
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="3.42rem" width="40%" />
          <Skeleton count={5} height="3.42rem" />
        </SkeletonTheme>
      </div>
    )
  }

  if (isError) {
    return (
      <>
        <PageHeader title={t('settings.title')} />
        <div className="egw-container">
          <GenericError errorMessage={t('default.generic_error')} />
        </div>
      </>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader title={t('settings.title')} />
      <div className="egw-container pt-0 flex-1 flex-col flex">
        <button
          id="settingsPersonalInfoLink"
          data-testid="settingsPersonalInfoLink"
          className={optionClassName}
          type="button"
          onClick={() => {
            onClickHandler(WEALTH_ROUTES.SETTINGS_PERSONAL_INFO.path)
          }}
        >
          {t('settings.options.personal_info')}
          <ChevronRightIcon className="h-6 w-6 fill-iconmuted" />
        </button>
        <button
          id="settingsPasskeyLink"
          data-testid="settingsPasskeyLink"
          className={optionClassName}
          type="button"
          onClick={() => {
            onClickHandler(WEALTH_ROUTES.SETTINGS_PASSKEY.path)
          }}
        >
          {t('settings.options.passkeys')}
          <ChevronRightIcon className="h-6 w-6 fill-iconmuted" />
        </button>
        {!checkingAccountClosed ? (
          <button
            id="settingsExternalAccountsLink"
            data-testid="settingsExternalAccountsLink"
            className={optionClassName}
            type="button"
            onClick={() => {
              onClickHandler(WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS.path)
            }}
          >
            {t('settings.options.external_accounts')}
            <ChevronRightIcon className="h-6 w-6 fill-iconmuted" />
          </button>
        ) : null}
        <button
          id="accountBeneficiariesLink"
          data-testid="accountBeneficiariesLink"
          className={optionClassName}
          type="button"
          onClick={() => {
            onClickHandler(WEALTH_ROUTES.SETTINGS_BENEFICIARIES.path)
          }}
        >
          {t('settings.options.beneficiaries')}
          <ChevronRightIcon className="h-6 w-6 fill-iconmuted" />
        </button>
      </div>
      <div className="egw-container">
        <p className="mt-10 text-meta">{t('settings.version', { version: config.appVersion })}</p>
      </div>
    </div>
  )
}
